import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CalendarMonth,
  CheckCircle,
  Close,
  Diversity3,
  Groups,
  LocationOn,
  PersonAddAlt,
  Share,
  VolunteerActivism,
} from "@mui/icons-material";

import "../../styles/community-meetups.css";
import { getPlainTextFromHtml } from "../../utils/richHtml";

const fallbackMeetups = [
  {
    id: "preview-atlanta",
    title: "Atlanta UC Community Meetup",
    city: "Atlanta Metro",
    state: "GA",
    venueName: "Local library / community room",
    addressLine: "Venue confirmation will be shared after registration",
    eventDate: "2026-07-19",
    startTime: "11:00",
    endTime: "12:30",
    timezone: "America/New_York",
    topic: "Second-income ideas, student internships, and family planning",
    description:
      "A friendly public walk-in session for families, students, professionals, and entrepreneurs to ask what UC can help with.",
    walkInAllowed: true,
    onlineAvailable: true,
    capacity: 35,
    registeredCount: 0,
    status: "scheduled",
  },
  {
    id: "preview-edison",
    title: "Edison UC Community Meetup",
    city: "Edison",
    state: "NJ",
    venueName: "Public library / city-center meeting room",
    addressLine: "Venue confirmation will be shared after registration",
    eventDate: "2026-08-02",
    startTime: "15:00",
    endTime: "16:30",
    timezone: "America/New_York",
    topic: "Immigration-aware career planning and attorney routing",
    description:
      "A community Q&A for opportunity seekers and professional partners. UC keeps legal topics educational and routes them to qualified attorneys.",
    walkInAllowed: true,
    onlineAvailable: true,
    capacity: 40,
    registeredCount: 0,
    status: "scheduled",
  },
  {
    id: "preview-online",
    title: "Online UC Community Orientation",
    city: "Online",
    state: "US",
    venueName: "Zoom / Google Meet",
    addressLine: "Meeting link will be shared after registration",
    eventDate: "2026-07-26",
    startTime: "19:30",
    endTime: "20:15",
    timezone: "America/New_York",
    topic: "How UC helps with opportunities, mentors, partners, and community services",
    description:
      "A short evening session for anyone who cannot attend a local city-center or library meetup.",
    walkInAllowed: false,
    onlineAvailable: true,
    capacity: 80,
    registeredCount: 0,
    status: "scheduled",
  },
];

const interestOptions = [
  "Second income / entrepreneurship",
  "Student internship / mentor",
  "Immigration help: O-1 / NIW / EB-1A",
  "Business or enterprise partnership",
  "Finance / insurance / family planning",
  "Healthcare / Ayurveda / wellness",
  "Education / art / music / tutoring",
  "Food / catering / baking",
  "Priest / ritual services",
  "Volunteer or community leader",
  "Not sure yet",
];

const attendanceOptions = [
  { value: "registered_seat", label: "Reserve my seat" },
  { value: "walk_in", label: "I may walk in" },
  { value: "online", label: "I prefer online" },
  { value: "need_help_deciding", label: "Help me decide" },
];

const emptyForm = {
  fullName: "",
  email: "",
  phone: "",
  cityZip: "",
  attendanceType: "registered_seat",
  primaryInterest: "Not sure yet",
  notes: "",
  consent: false,
  website: "",
};

const parseMeetupDate = (meetup) => {
  if (!meetup?.eventDate) return null;
  const time = meetup.startTime || "00:00";
  const dateValue = String(meetup.eventDate).slice(0, 10);
  const date = new Date(`${dateValue}T${time}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMeetupDate = (meetup) => {
  const date = parseMeetupDate(meetup);
  if (!date) return "Date to be announced";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatMeetupTime = (meetup) => {
  if (!meetup?.startTime) return "Time to be announced";
  const start = parseMeetupDate(meetup);
  const end = meetup.endTime
    ? new Date(`${String(meetup.eventDate).slice(0, 10)}T${meetup.endTime}`)
    : null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (!start) return "Time to be announced";
  return end && !Number.isNaN(end.getTime())
    ? `${formatter.format(start)} – ${formatter.format(end)}`
    : formatter.format(start);
};

const toGoogleCalendarDate = (date) =>
  date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const buildGoogleCalendarLink = (meetup) => {
  const start = parseMeetupDate(meetup);
  if (!start) return "";
  const end = meetup.endTime
    ? new Date(`${meetup.eventDate}T${meetup.endTime}`)
    : new Date(start.getTime() + 60 * 60 * 1000);
  const dates = `${toGoogleCalendarDate(start)}/${toGoogleCalendarDate(end)}`;
  const details = [
    meetup.description,
    "",
    "Please check your UC confirmation for final venue or meeting-link details.",
  ].join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: meetup.title || "UC Community Meetup",
    dates,
    details,
    location: [meetup.venueName, meetup.addressLine, meetup.city, meetup.state]
      .filter(Boolean)
      .join(", "),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildMeetupShareUrl = (meetup) => {
  const key = meetup?.slug || meetup?.id;
  return `${window.location.origin}/2.0/community-meetup/${encodeURIComponent(key)}/register`;
};

const buildEventShareUrl = (eventId) =>
  `${window.location.origin}/2.0/event/${encodeURIComponent(eventId)}/register`;

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  window.prompt("Copy this meetup share link:", value);
};

const getSeatsText = (meetup) => {
  if (!meetup.capacity) return "Open registration";
  const remaining = Math.max(0, Number(meetup.capacity) - Number(meetup.registeredCount || 0));
  return `${remaining} of ${meetup.capacity} seats left`;
};

const CommunityMeetups = ({ variant = "home", refreshKey = 0, calendarEvents = [] }) => {
  const navigate = useNavigate();
  const [meetups, setMeetups] = useState([]);
  const [loadStatus, setLoadStatus] = useState("loading");
  const [selectedMeetup, setSelectedMeetup] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitState, setSubmitState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    let mounted = true;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/community-meetups`)
      .then((response) => {
        if (!mounted) return;
        const items = Array.isArray(response.data?.meetups) ? response.data.meetups : [];
        setMeetups(items);
        setLoadStatus("ready");
      })
      .catch(() => {
        if (!mounted) return;
        setMeetups([]);
        setLoadStatus("error");
      });

    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const displayMeetups = useMemo(
    () => (meetups.length ? meetups : fallbackMeetups),
    [meetups]
  );
  const isPreviewMode = loadStatus === "error" && meetups.length === 0;
  const featuredMeetups = variant === "compact" ? displayMeetups.slice(0, 2) : displayMeetups;
  const featuredItems = useMemo(() => {
    const meetupItems = featuredMeetups.map((meetup) => ({ ...meetup, cardKind: "meetup" }));
    if (variant !== "calendar") return meetupItems;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventItems = (Array.isArray(calendarEvents) ? calendarEvents : [])
      .filter((event) => {
        const date = parseMeetupDate(event);
        const status = String(event.status || "").toLowerCase();
        return date && date >= today && !["cancelled", "canceled"].includes(status);
      })
      .map((event) => {
        const summary = getPlainTextFromHtml(event.description).replace(/\s+/g, " ").trim();
        return {
        ...event,
        id: `event-${event.id}`,
        sourceId: event.id,
        cardKind: "event",
        title: event.eventTitle || "Udyog Connect Event",
        topic: summary.length > 180 ? `${summary.slice(0, 177).trim()}…` : summary,
        venueName: event.venue || "Venue to be announced",
        addressLine: event.slotRegistrationEnabled
          ? `${event.slots?.length || 0} selectable registration slots`
          : "Native UC event registration",
        city: event.venue || "Venue to be announced",
        state: "",
        walkInAllowed: false,
        onlineAvailable: /online|zoom|google meet/i.test(String(event.venue || "")),
        registeredCount: Number(event.registeredSeats || 0),
        };
      });

    return [...eventItems, ...meetupItems].sort((first, second) => {
      const firstDate = parseMeetupDate(first)?.getTime() || Number.MAX_SAFE_INTEGER;
      const secondDate = parseMeetupDate(second)?.getTime() || Number.MAX_SAFE_INTEGER;
      return firstDate - secondDate;
    });
  }, [calendarEvents, featuredMeetups, variant]);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openRegistration = (meetup) => {
    setSelectedMeetup(meetup);
    setForm({
      ...emptyForm,
      attendanceType: meetup.onlineAvailable && !meetup.walkInAllowed ? "online" : "registered_seat",
    });
    setSubmitState({ status: "idle", message: "" });
  };

  const buildItemShareUrl = (item) => item.cardKind === "event"
    ? buildEventShareUrl(item.sourceId)
    : buildMeetupShareUrl(item);

  const copyShareLink = async (item) => {
    await copyText(buildItemShareUrl(item));
    setSubmitState({ status: "success", message: "Share link copied." });
  };

  const openSocialShare = (item, channel) => {
    const shareUrl = buildItemShareUrl(item);
    const text = `${item.title} | ${item.cardKind === "event" ? item.eventCategory || "UC Event" : "UC Community Meetup"}`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(text);
    const urls = {
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    };
    window.open(urls[channel], "_blank", "noopener,noreferrer");
  };

  const closeRegistration = () => {
    setSelectedMeetup(null);
    setSubmitState({ status: "idle", message: "" });
  };

  const submitRegistration = async (event) => {
    event.preventDefault();

    if (isPreviewMode) {
      setSubmitState({
        status: "error",
        message: "Registration service is not available yet. Please start the local backend and migration.",
      });
      return;
    }

    if (!form.fullName.trim() || !form.email.trim() || !form.consent) {
      setSubmitState({
        status: "error",
        message: "Please enter your name, email, and consent before registering.",
      });
      return;
    }

    setSubmitState({ status: "submitting", message: "Registering you for the meetup…" });
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/community-meetups/register`,
        {
          ...form,
          meetupId: selectedMeetup.id,
          source: `uc_${variant}_community_meetup`,
        }
      );
      setSubmitState({
        status: "success",
        message: response.data?.message || "You are registered for the UC Community Meetup.",
      });
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error?.response?.data?.message || "Unable to register right now.",
      });
    }
  };

  const goToOpportunityProfile = () => {
    navigate("/2.0/login", {
      state: {
        from: {
          pathname: "/2.0/udyogconnect-home",
          state: { openOpportunityProfile: true },
        },
      },
    });
  };

  return (
    <section className={`uc-meetups-section ${variant}`} id="uc-community-meetups">
      <div className="uc-meetups-hero">
        <div>
          <span className="uc-meetups-kicker">
            <Diversity3 fontSize="inherit" />
            {variant === "calendar" ? "Upcoming UC Programs" : "UC Community Meetups"}
          </span>
          <h2>
            {variant === "calendar"
              ? "Upcoming events and community meetups — all in one place."
              : "Meet UC near you — city centers, libraries, and online sessions."}
          </h2>
          {variant === "calendar" ? (
            <p>
              Discover UC events, workshops, volunteer opportunities, and public
              community sessions. Choose a card to view details or register.
            </p>
          ) : (
            <p>
              Join a free bi-weekly public session to discuss second-income ideas,
              student internships, immigration-aware routing, family planning,
              priest/ritual services, and community partnerships.
            </p>
          )}
        </div>

        <div className="uc-meetups-hero-card">
          <strong>{variant === "calendar" ? "Find your next UC activity" : "Free public walk-in"}</strong>
          <span>
            {variant === "calendar"
              ? "Event and meetup registration links are available directly from each card."
              : "Registering helps us prepare seats, volunteers, and the right sector lead."}
          </span>
          <button
            type="button"
            onClick={() => variant === "calendar"
              ? document.getElementById("uc-upcoming-program-grid")?.scrollIntoView({ behavior: "smooth", block: "start" })
              : openRegistration(displayMeetups[0])}
          >
            <PersonAddAlt fontSize="small" />
            {variant === "calendar" ? "Browse upcoming activities" : "Register for next session"}
          </button>
        </div>
      </div>

      {isPreviewMode && (
        <div className="uc-meetups-preview-note">
          Preview data is showing because the local meetup API is not responding yet.
        </div>
      )}

      <div className="uc-meetups-grid" id={variant === "calendar" ? "uc-upcoming-program-grid" : undefined}>
        {featuredItems.map((meetup) => {
          const isEvent = meetup.cardKind === "event";
          return (
          <article className={`uc-meetup-card${isEvent ? " uc-event-card" : ""}`} key={`${meetup.cardKind}-${meetup.id}`}>
            {variant === "calendar" && (
              <span className={`uc-calendar-card-type ${isEvent ? "event" : "meetup"}`}>
                {isEvent ? meetup.eventCategory || "UC Event" : "Community Meetup"}
              </span>
            )}
            <div className="uc-meetup-card-top">
              <span className="uc-meetup-date-badge">
                <CalendarMonth fontSize="small" />
                {formatMeetupDate(meetup)}
              </span>
              <span className="uc-meetup-seat-badge">{getSeatsText(meetup)}</span>
            </div>

            <h3>{meetup.title}</h3>
            <p>{meetup.topic}</p>

            <div className="uc-meetup-meta">
              <span>
                <LocationOn fontSize="small" />
                {isEvent ? meetup.venueName : `${meetup.city}${meetup.state ? `, ${meetup.state}` : ""}`}
              </span>
              <span>
                <Groups fontSize="small" />
                {isEvent
                  ? meetup.whoCanApply || "Anyone can apply"
                  : meetup.walkInAllowed ? "Walk-ins welcome" : "Registration required"}
              </span>
              <span>
                <CalendarMonth fontSize="small" />
                {formatMeetupTime(meetup)}
              </span>
            </div>

            <div className="uc-meetup-location">
              <strong>{meetup.venueName || "Venue to be announced"}</strong>
              <small>{meetup.addressLine || "Address will be shared after registration."}</small>
            </div>

            <div className="uc-meetup-tags">
              {isEvent ? (
                <>
                  <span>{meetup.eventCategory || "UC Event"}</span>
                  {meetup.slotRegistrationEnabled && <span>Multiple slots</span>}
                  <span>{meetup.status || "Registration open"}</span>
                </>
              ) : (
                <>
                  {meetup.onlineAvailable && <span>Online option</span>}
                  <span>Students welcome</span>
                  <span>Families welcome</span>
                </>
              )}
            </div>

            <button
              type="button"
              className="uc-meetup-register-btn"
              onClick={() => isEvent
                ? navigate(`/2.0/event/${encodeURIComponent(meetup.sourceId)}/register`)
                : openRegistration(meetup)}
            >
              {isEvent ? "View details / Register" : "Register / RSVP"}
            </button>

            <div className="uc-meetup-share-actions">
              <button type="button" onClick={() => copyShareLink(meetup)}>
                <Share fontSize="small" />
                Copy share link
              </button>
              <button type="button" onClick={() => openSocialShare(meetup, "whatsapp")}>
                WhatsApp
              </button>
              <button type="button" onClick={() => openSocialShare(meetup, "facebook")}>
                Facebook
              </button>
              <button type="button" onClick={() => openSocialShare(meetup, "linkedin")}>
                LinkedIn
              </button>
            </div>
          </article>
          );
        })}
        {featuredItems.length === 0 && (
          <div className="uc-meetups-empty-card">No upcoming UC activities are scheduled yet.</div>
        )}
      </div>

      <div className="uc-meetups-footer-actions">
        <button
          type="button"
          onClick={() => variant === "calendar"
            ? document.querySelector(".calendar-filter-panel")?.scrollIntoView({ behavior: "smooth", block: "start" })
            : navigate("/2.0/calendar")}
        >
          {variant === "calendar" ? "Browse calendar view" : "View all calendar events"}
        </button>
        <button type="button" className="secondary" onClick={goToOpportunityProfile}>
          Build my Opportunity Profile
        </button>
      </div>

      {selectedMeetup && (
        <div className="uc-meetup-modal-backdrop" role="presentation" onClick={closeRegistration}>
          <div
            className="uc-meetup-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="uc-meetup-register-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="uc-meetup-modal-close"
              onClick={closeRegistration}
              aria-label="Close community meetup registration"
            >
              <Close fontSize="small" />
            </button>

            <div className="uc-meetup-modal-header">
              <span className="uc-meetups-kicker">
                <VolunteerActivism fontSize="inherit" />
                Free UC session
              </span>
              <h2 id="uc-meetup-register-title">Register for {selectedMeetup.title}</h2>
              <p>
                {formatMeetupDate(selectedMeetup)} · {formatMeetupTime(selectedMeetup)} ·{" "}
                {selectedMeetup.city}{selectedMeetup.state ? `, ${selectedMeetup.state}` : ""}
              </p>
            </div>

            {submitState.status === "success" ? (
              <div className="uc-meetup-success">
                <CheckCircle />
                <h3>{submitState.message}</h3>
                <p>
                  We’ll use your interest area to prepare the right UC conversation.
                  You can also create a profile so UC can follow your long-term path.
                </p>
                <div className="uc-meetup-success-actions">
                  {buildGoogleCalendarLink(selectedMeetup) && (
                    <a
                      href={buildGoogleCalendarLink(selectedMeetup)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Add to Google Calendar
                    </a>
                  )}
                  <button type="button" onClick={goToOpportunityProfile}>
                    Build my Opportunity Profile
                  </button>
                </div>
              </div>
            ) : (
              <form className="uc-meetup-form" onSubmit={submitRegistration}>
                <input
                  className="uc-meetup-honeypot"
                  tabIndex="-1"
                  autoComplete="off"
                  value={form.website}
                  onChange={(event) => updateForm("website", event.target.value)}
                  aria-hidden="true"
                />

                <div className="uc-meetup-form-grid">
                  <label>
                    Full name
                    <input
                      value={form.fullName}
                      onChange={(event) => updateForm("fullName", event.target.value)}
                      placeholder="Your name"
                      required
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => updateForm("email", event.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      value={form.phone}
                      onChange={(event) => updateForm("phone", event.target.value)}
                      placeholder="Optional"
                    />
                  </label>
                  <label>
                    City / ZIP
                    <input
                      value={form.cityZip}
                      onChange={(event) => updateForm("cityZip", event.target.value)}
                      placeholder="Example: Edison 08820"
                    />
                  </label>
                  <label>
                    Attendance
                    <select
                      value={form.attendanceType}
                      onChange={(event) => updateForm("attendanceType", event.target.value)}
                    >
                      {attendanceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Main interest
                    <select
                      value={form.primaryInterest}
                      onChange={(event) => updateForm("primaryInterest", event.target.value)}
                    >
                      {interestOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="uc-meetup-notes">
                  What help are you looking for from UC?
                  <textarea
                    value={form.notes}
                    onChange={(event) => updateForm("notes", event.target.value)}
                    placeholder="Optional: Tell us what brought you to this meetup."
                    maxLength={1000}
                  />
                </label>

                <label className="uc-meetup-consent">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(event) => updateForm("consent", event.target.checked)}
                    required
                  />
                  <span>
                    I agree UC may contact me about this meetup and related community
                    opportunity support.
                  </span>
                </label>

                {submitState.message && (
                  <div className={`uc-meetup-message ${submitState.status}`}>
                    {submitState.message}
                  </div>
                )}

                <button
                  type="submit"
                  className="uc-meetup-submit"
                  disabled={submitState.status === "submitting"}
                >
                  <PersonAddAlt fontSize="small" />
                  {submitState.status === "submitting" ? "Registering…" : "Confirm Registration"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CommunityMeetups;
